import * as Sentry from '@sentry/react';
import { StytchB2B } from '@stytch/react/b2b';
import { StytchB2BUIConfig, StytchEventType } from '@stytch/vanilla-js';
import PageContainer from './PageContainer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../api';
import { AxiosError } from 'axios';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../auth';
import { setAnalyticsIdentity } from 'shared-utils/analytics';
import {
  StytchLoginContainer,
  stytchLoginApiStyles,
  STYTCH_CONTAINER_WIDTH,
} from 'design-system/src/components/StytchLoginContainer';

import { B2BProducts, AuthFlowType, B2BOAuthProviders } from '@stytch/vanilla-js';
import Button from 'design-system/src/components/Button/Button';
import { FlexColumn, FlexRow } from 'design-system/src/components/Layout';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { loginLinkExpiredCopy } from 'design-system/src/sharedCopy';
import { replaceTextOnPage } from '../utils';

const domain = import.meta.env.VITE_STYTCH_REDIRECT_DOMAIN;

const passwordRedirectURL = `${domain}/password-reset`;
const defaultRedirect = '/';
// These are normal authentication errors - let stytch SDK surface the error
const SURFACE_STYTCH_ERROR_CODES = ['unauthorized_credentials', 'member_reset_password', 'member_password_not_found'];

interface FormData {
  token: string;
  isImpersonated?: boolean;
}

interface LoginResponse {
  member_id: string;
  email: string;
  practice_key: string;
  impersonator_member_id?: string;
  impersonator_email?: string;
}

interface ErrorResponse {
  error?: string;
}

type LoginOption = 'oauth' | 'password' | 'magic-link';
// Subtract 24px padding from each side
const STYTCH_BUTTON_WIDTH = `calc(${STYTCH_CONTAINER_WIDTH} - 48px)`;

const LoginContainer = styled.div`
  width: ${STYTCH_CONTAINER_WIDTH};
  margin: 0 auto;
`;
const LoginOptionsContainer = styled(FlexColumn)`
  align-items: center;
  margin-top: 24px;
`;
const LoginOptionButton = styled(Button)`
  width: ${STYTCH_BUTTON_WIDTH};
`;
// Imitating stytch's divider row styling
const DividerRow = styled(FlexRow)`
  width: ${STYTCH_BUTTON_WIDTH};
  align-items: center;
`;
const DividerBar = styled.div<{ $side: 'left' | 'right' }>`
  ${({ $side }) => ($side === 'left' ? 'margin-right: 6px' : 'margin-left: 6px')};
  border-bottom: 1px solid var(--border-gray);
  -webkit-box-flex: 1;
  flex-grow: 1;
`;

/**
 * Set FullStory identity for the current user.
 * https://developer.fullstory.com/browser/identification/identify-users/
 */
const setFSIdentity = (response: LoginResponse) => {
  if (response.impersonator_member_id) {
    setAnalyticsIdentity(
      response.impersonator_member_id,
      response.impersonator_email!,
      `${response.impersonator_email} as ${response.email}`,
      {
        practice_key: response.practice_key,
      },
    );
  } else {
    setAnalyticsIdentity(response.member_id, response.email, response.email, {
      practice_key: response.practice_key,
    });
  }
};

const useLogin = (postLoginRedirect: string = defaultRedirect) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const login = useMutation<LoginResponse, AxiosError<ErrorResponse>, FormData>({
    mutationFn: async (data) => {
      const endpoint = data.isImpersonated ? 'login-impersonated' : 'login';
      const res = await client.post<LoginResponse>(endpoint, { token: data.token });
      return res.data;
    },
    onSuccess: async (response) => {
      setFSIdentity(response);

      await queryClient.invalidateQueries({ queryKey: ['checkLogin'] });
      navigate(postLoginRedirect);
    },
  });

  return login;
};

export const Login = () => {
  const authContext = React.useContext(AuthContext);
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeLoginOption, setLoginOption] = useState<LoginOption>('oauth');

  const postLoginRedirect = useRef(searchParams.get('next') || undefined);

  const config = slug
    ? getConfig(AuthFlowType.Organization, postLoginRedirect.current, activeLoginOption)
    : getConfig(AuthFlowType.Discovery, postLoginRedirect.current, activeLoginOption);

  const login = useLogin(postLoginRedirect.current);

  const callbacks = {
    onEvent: (event: { type: StytchEventType; data: any }) => {
      if (
        event.type === StytchEventType.B2BDiscoveryIntermediateSessionExchange ||
        event.type === StytchEventType.B2BOAuthAuthenticate ||
        event.type === StytchEventType.B2BPasswordAuthenticate ||
        event.type === StytchEventType.B2BMagicLinkAuthenticate ||
        event.type === StytchEventType.B2BSSOAuthenticate
      ) {
        login.mutate({ token: event.data.session_token });
      } else if (event.type == StytchEventType.B2BImpersonationAuthenticate) {
        login.mutate({ token: event.data.session_token, isImpersonated: true });
      }
    },
    onError: (data: any) => {
      console.error(data);
      if (SURFACE_STYTCH_ERROR_CODES.some((code) => data.message.includes(code))) {
        return;
      }
      setIsError(true);
      if (data.message.includes('unable_to_auth_magic_link')) {
        setErrorMessage(loginLinkExpiredCopy);
      } else {
        Sentry.captureException(data);
      }
    },
  };

  if (authContext.auth === true) {
    return <Navigate to={postLoginRedirect.current || defaultRedirect} />;
  }

  useEffect(() => {
    // we want to improve this copy, but it's defined in a package that we can't easily modify,
    // and there doesn't appear to be any events or signals that we can listen to to know when
    // the page has updated to show this text.
    // therefore, we need to check the page whenever the login option changes
    // and replace the text when it appears.
    const timeout = setTimeout(() => {
      // Stytch header text
      replaceTextOnPage('Sign up or log in', 'Log in to Tandem', 'stytch-b2b-ui div[size="header"][color="primary"]');
      // Stytch password reset link
      replaceTextOnPage(
        'Sign up or reset password',
        'Set up or reset a password',
        'stytch-b2b-ui form div[size="body"][color="secondary"]',
      );
    }, 100);

    return () => clearTimeout(timeout);
  }, [activeLoginOption]);

  return (
    <PageContainer background="light" navbarPlacement="top">
      <StytchLoginContainer>
        {isError ? (
          <PageLoadError overrideErrorMessage={errorMessage} />
        ) : (
          <StytchB2B config={config} callbacks={callbacks} styles={stytchLoginApiStyles} />
        )}
      </StytchLoginContainer>
      <LoginContainer>
        <LoginOptionsContainer>
          <DividerRow>
            <DividerBar $side="left" />
            <div>or</div>
            <DividerBar $side="right" />
          </DividerRow>
          {activeLoginOption !== 'oauth' && (
            <LoginOptionButton onClick={() => setLoginOption('oauth')}>
              Log in with Microsoft or Google
            </LoginOptionButton>
          )}
          {activeLoginOption !== 'password' && (
            <LoginOptionButton
              // style as 'primary' if this is the top button in the stack
              variant={activeLoginOption === 'oauth' ? 'primary' : 'tertiary'}
              onClick={() => setLoginOption('password')}
            >
              Log in with password
            </LoginOptionButton>
          )}
          {activeLoginOption !== 'magic-link' && (
            <LoginOptionButton variant="tertiary" onClick={() => setLoginOption('magic-link')}>
              Log in with email verification link
            </LoginOptionButton>
          )}
        </LoginOptionsContainer>
      </LoginContainer>
    </PageContainer>
  );
};

export const PasswordReset = () => {
  const login = useLogin();

  const callbacks = {
    onEvent: (event: { type: StytchEventType; data: any }) => {
      if (
        event.type === StytchEventType.B2BPasswordResetByEmail ||
        event.type === StytchEventType.B2BPasswordResetBySession ||
        // event after choosing an org from the discovery flow - with session token
        event.type === StytchEventType.B2BDiscoveryIntermediateSessionExchange
      ) {
        login.mutate({ token: event.data.session_token });
      }
    },
    onError: (data: any) => {
      console.error(data);
    },
  };
  return (
    <PageContainer title="Reset Password" background="light" navbarPlacement="top">
      <StytchLoginContainer>
        <StytchB2B config={getConfig(AuthFlowType.PasswordReset)} callbacks={callbacks} styles={stytchLoginApiStyles} />
      </StytchLoginContainer>
    </PageContainer>
  );
};

const getConfig = (
  type: AuthFlowType,
  postLoginRedirect: string | undefined = undefined,
  activeLoginOption: LoginOption = 'oauth',
): StytchB2BUIConfig => {
  const loginRedirectURL = postLoginRedirect
    ? `${domain}/login?next=${encodeURIComponent(postLoginRedirect)}`
    : `${domain}/login`;

  let loginProduct = undefined;
  if (activeLoginOption === 'magic-link') {
    loginProduct = B2BProducts.emailMagicLinks;
  } else if (activeLoginOption === 'password') {
    loginProduct = B2BProducts.passwords;
  } else {
    loginProduct = B2BProducts.oauth;
  }

  switch (type) {
    case AuthFlowType.Organization:
      return {
        authFlowType: AuthFlowType.Organization,
        // sso must come before oauth because stytch renders a blank row if SSO is not configured
        products: [B2BProducts.sso, loginProduct],
        emailMagicLinksOptions: {
          loginRedirectURL: loginRedirectURL,
        },
        ssoOptions: {
          loginRedirectURL: loginRedirectURL,
        },
        oauthOptions: {
          providers: [B2BOAuthProviders.Microsoft, B2BOAuthProviders.Google],
          loginRedirectURL: loginRedirectURL,
        },
        passwordOptions: {
          loginRedirectURL: loginRedirectURL,
          resetPasswordRedirectURL: passwordRedirectURL,
          resetPasswordExpirationMinutes: 20,
        },
        sessionOptions: {
          sessionDurationMinutes: 60,
        },
        // Skip the org selection and log the user in directly if they have a single membership,
        // unless they can join another org via an invite or JIT provisioning
        directLoginForSingleMembership: {
          status: true,
          ignoreInvites: false,
          ignoreJitProvisioning: false,
        },
      };
    case AuthFlowType.Discovery:
      return {
        authFlowType: AuthFlowType.Discovery,
        products: [loginProduct],
        emailMagicLinksOptions: {
          discoveryRedirectURL: loginRedirectURL,
        },
        oauthOptions: {
          providers: [B2BOAuthProviders.Microsoft, B2BOAuthProviders.Google],
          discoveryRedirectURL: loginRedirectURL,
        },
        passwordOptions: {
          loginRedirectURL: loginRedirectURL,
          resetPasswordRedirectURL: passwordRedirectURL,
          resetPasswordExpirationMinutes: 20,
        },
        sessionOptions: {
          sessionDurationMinutes: 60,
        },
        // Skip the org selection and log the user in directly if they have a single membership,
        // unless they can join another org via an invite or JIT provisioning
        directLoginForSingleMembership: {
          status: true,
          ignoreInvites: false,
          ignoreJitProvisioning: false,
        },
      };
    case AuthFlowType.PasswordReset:
      return {
        authFlowType: AuthFlowType.PasswordReset,
        products: [B2BProducts.passwords],
        sessionOptions: {
          sessionDurationMinutes: 60,
        },
      };
  }
};
